import {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {
  createStreamingRecognizer,
  type CreateStreamingRecognizerOptions,
} from 'src/utils/speech-to-text';
import type {
  GetPresignedUrl,
  MicDevice,
  MicPermission,
  PresignedUrlResponse,
  SttError,
  SttState,
  StreamingRecognizer,
} from 'src/types/speech-to-text';
import * as askAiApi from 'src/utils/redux-api-ask-ai';
import type {Dispatch} from 'src/reducers';
import logger from 'src/utils/logger';


export interface UseSpeechToTextOptions {
  /** Current uncommitted guess (replace the live region with this). */
  onInterim: (text: string) => void;
  /** Finalized, corrected chunk to commit. */
  onFinal: (text: string) => void;
  /** Fired when capture/streaming fails (e.g. surface a toast). Not called for
   *  the user simply stopping/cancelling. */
  onError?: (err: SttError) => void;
  /** BCP-47 language code, default 'en-US'. */
  lang?: string;
}

export interface UseSpeechToText {
  state: SttState;
  /** Available input mics (labels populated only once permission is granted). */
  devices: MicDevice[];
  /** Mic permission; drives the device-selector vs "permission needed" UI. */
  permission: MicPermission;
  /** Currently chosen mic; '' means the system default. */
  selectedDeviceId: string;
  setSelectedDeviceId: (deviceId: string) => void;
  /** Open mic + provider socket; resolves to `recording` once streaming. */
  start: () => Promise<void>;
  /** Stop and keep the transcript (✓). */
  stop: () => Promise<void>;
  /** Abort and discard (✕). */
  cancel: () => void;
}

// Chrome injects synthetic 'default'/'communications' aggregate entries. We
// model "default" ourselves (selectedDeviceId === '') so they're filtered out.
function isRealMic(d: MediaDeviceInfo): boolean {
  return (
    d.kind === 'audioinput' &&
    d.deviceId !== 'default' &&
    d.deviceId !== 'communications'
  );
}

function toSttError(err: unknown): SttError {
  if (
    err &&
    typeof err === 'object' &&
    'code' in err &&
    'message' in err &&
    typeof (err as SttError).message === 'string'
  ) {
    return err as SttError;
  }
  return {code: 'unknown', message: 'Speech recognition failed'};
}

/**
 * Orchestrates the streaming speech-to-text session. The recognizer owns the
 * mic + provider WebSocket and emits interim/final transcript through the
 * callbacks; this hook only drives idle → connecting → recording and surfaces
 * errors. On error it returns to idle (composer visible) and exposes `error`.
 */
export function useSpeechToText(opts: UseSpeechToTextOptions): UseSpeechToText {
  const {onInterim, onFinal, onError, lang} = opts;
  const [state, setState] = useState<SttState>('idle');
  const [devices, setDevices] = useState<MicDevice[]>([]);
  const [permission, setPermission] = useState<MicPermission>('unknown');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

  // Enumerate mics + track permission. Labels are empty until permission is
  // granted, so we re-enumerate whenever the grant arrives (effect mount, the
  // Permissions API onchange, devicechange, and after a successful capture).
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      return;
    }
    let cancelled = false;

    const refresh = async () => {
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) {
          return;
        }
        setDevices(
          all
            .filter(isRealMic)
            .map((d) => ({deviceId: d.deviceId, label: d.label})),
        );
      } catch (err) {
        logger.error('useSpeechToText: enumerateDevices failed', err);
      }
    };
    void refresh();
    navigator.mediaDevices.addEventListener?.('devicechange', refresh);

    // Permissions API is Chromium-only for 'microphone'; degrade to 'unknown'
    // elsewhere and rely on reactive detection in start().
    let permStatus: PermissionStatus | null = null;
    const onPermChange = () => {
      if (!permStatus) {
        return;
      }
      setPermission(permStatus.state as MicPermission);
      if (permStatus.state === 'granted') {
        void refresh();
      }
    };
    navigator.permissions
      ?.query?.({name: 'microphone' as PermissionName})
      .then((status) => {
        if (cancelled) {
          return;
        }
        permStatus = status;
        setPermission(status.state as MicPermission);
        status.addEventListener?.('change', onPermChange);
        if (status.state === 'granted') {
          void refresh();
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      navigator.mediaDevices.removeEventListener?.('devicechange', refresh);
      permStatus?.removeEventListener?.('change', onPermChange);
    };
  }, []);

  const dispatch = useDispatch<Dispatch>();
  // Mint a fresh presigned Transcribe URL via the grace API (session cookie
  // rides the proxy). Query keys are the snake_case the backend expects.
  const getPresignedUrl: GetPresignedUrl = useCallback(
    (req) =>
      dispatch(
        askAiApi.get<PresignedUrlResponse>('stt/presigned-url', {
          language_code: req.languageCode,
          sample_rate: req.sampleRate,
          media_encoding: req.mediaEncoding,
        }),
      ),
    [dispatch],
  );

  const recognizerRef = useRef<StreamingRecognizer | null>(null);
  const onInterimRef = useRef(onInterim);
  onInterimRef.current = onInterim;
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const recognizerOptsRef = useRef<CreateStreamingRecognizerOptions>({
    lang,
    getPresignedUrl,
    deviceId: selectedDeviceId,
  });
  recognizerOptsRef.current = {
    lang,
    getPresignedUrl,
    deviceId: selectedDeviceId,
  };

  const start = useCallback(async () => {
    if (recognizerRef.current) {
      return;
    }
    setState('connecting');

    const recognizer = createStreamingRecognizer(recognizerOptsRef.current);
    recognizerRef.current = recognizer;
    try {
      await recognizer.start({
        onInterim: (text) => onInterimRef.current(text),
        onFinal: (text) => onFinalRef.current(text),
        onError: (err) => {
          logger.error('speech-to-text stream error', err);
          recognizer.cancel();
          recognizerRef.current = null;
          onErrorRef.current?.(err);
          setState('idle');
        },
      });
      setState('recording');
      // Capture succeeded → permission is granted; re-enumerate so device
      // labels (hidden until grant) populate the selector.
      setPermission('granted');
      if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
        navigator.mediaDevices
          .enumerateDevices()
          .then((all) =>
            setDevices(
              all
                .filter(isRealMic)
                .map((d) => ({deviceId: d.deviceId, label: d.label})),
            ),
          )
          .catch(() => undefined);
      }
    } catch (err) {
      recognizer.cancel();
      recognizerRef.current = null;
      logger.error('speech-to-text start failed', err);
      const sttErr = toSttError(err);
      if (sttErr.code === 'permission-denied') {
        setPermission('denied');
      }
      onErrorRef.current?.(sttErr);
      setState('idle');
    }
  }, []);

  const stop = useCallback(async () => {
    const recognizer = recognizerRef.current;
    if (!recognizer) {
      return;
    }
    recognizerRef.current = null;
    setState('stopping');
    try {
      await recognizer.stop();
    } catch (err) {
      logger.error('speech-to-text stop failed', err);
      onErrorRef.current?.(toSttError(err));
    } finally {
      setState('idle');
    }
  }, []);

  const cancel = useCallback(() => {
    recognizerRef.current?.cancel();
    recognizerRef.current = null;
    setState('idle');
  }, []);

  // Discard any active session on unmount.
  useEffect(
    () => () => {
      recognizerRef.current?.cancel();
      recognizerRef.current = null;
    },
    [],
  );

  return {
    state,
    devices,
    permission,
    selectedDeviceId,
    setSelectedDeviceId,
    start,
    stop,
    cancel,
  };
}
